import { FinalExecutionStatus } from "@near-js/types";
import { FunctionCallAction, WalletSelector } from "@near-wallet-selector/core";
import { Big } from "big.js";
import { t } from "i18next";

import { ModalType } from "@/components/Modals/Warning";
import { ToastService } from "@/components/ToastService";
import { EMPTY_STRING, TOAST_ID } from "@/constants";
import { NetworkIconMap } from "@/constants/map";
import { useAirdrops, useTokens } from "@/hooks/store";
import { Tokens } from "@/hooks/store/useTokens";
import { ModalContextProps, NFTPurchasedParams } from "@/interfaces/modals";
import { Network } from "@/interfaces/network";
import { NFTMetadata } from "@/interfaces/nft";
import { hapiApi } from "@/services/api/hapi";
import { config } from "@/services/config";
import { chunkArray, delay, displayAmount, formatTokenAmount, hexToBytes, isNotNullOrUndefined, toMap } from "@/utils";
import { getErrorsFromLogs } from "@/utils/nearTrx";

import { NearAirdropContract } from "../contracts/near/Airdrops";
import { FungibleTokenContract, NEAR_TOKEN, ONE_YOCTO } from "../contracts/near/FungibleToken";
import { convertGas } from "../contracts/near/helpers";
import { NearTotalScoreContract } from "../contracts/near/TotalScore";
import { NearRPCProviderService } from "../rpc/near";

const MAX_IDS_PER_TRANSACTION = 20;
const MIN_NEAR_BALANCE = "250000000000000000000000";

const mintScore = async ({
  selector,
  address,
  transaction,
  isUpdateMint = false,
}: {
  selector: WalletSelector;
  address: string;
  transaction?: string;
  isUpdateMint?: boolean;
}) => {
  const name = isUpdateMint ? "re-mint" : "mint";
  const icon = NetworkIconMap.NEAR;

  const rpcProvider = new NearRPCProviderService();

  try {
    ToastService.loading({ text: t("Messages.LoadingAction", { name }), id: TOAST_ID.MINT });
    const feeContractResponse = await rpcProvider.viewFunction("get_prices", config.near.score, {});
    if (!feeContractResponse?.[0] || !feeContractResponse?.[1]) {
      ToastService.update({ text: "Can't get fee from contract state", id: TOAST_ID.MINT, icon });
      return false;
    }
    const parsedFee = isUpdateMint ? feeContractResponse[1] : feeContractResponse[0];
    const account = await rpcProvider.viewAccount(address);
    const accountBalance = account?.amount || "0";

    if (Big(accountBalance.toString()).lte(parsedFee)) {
      ToastService.update({
        text: `Insufficient Balance - ${formatTokenAmount(parsedFee, NEAR_TOKEN.decimals)} ${NEAR_TOKEN.symbol}`,
        id: TOAST_ID.MINT,
        icon,
      });
      return false;
    }

    if (Big(accountBalance.toString()).lte(MIN_NEAR_BALANCE)) {
      ToastService.update({
        text: `Current mint cost is ${formatTokenAmount(parsedFee, NEAR_TOKEN.decimals)} ${NEAR_TOKEN.symbol}, but you should have at least ${formatTokenAmount(MIN_NEAR_BALANCE, NEAR_TOKEN.decimals)} ${NEAR_TOKEN.symbol} for sending transaction`,
        id: TOAST_ID.MINT,
        icon,
      });
      return false;
    }

    let trxHash = transaction;

    if (!trxHash) {
      const wallet = await selector.wallet();
      const data = await hapiApi.getScoreV1({
        network: Network.NEAR,
        address: undefined,
        tg: undefined,
        chainId: null,
        nearAddress: address,
      });
      if (data.errorCode === 1) {
        console.error(`Error, while ${name} trust score \n\n`);
        hapiApi.logError({
          nearAddress: address,
          action: isUpdateMint ? "UPDATE_MINT" : "MINT",
          error: "No trust score",
        });
        return false;
      }

      const trx = await wallet.signAndSendTransaction({
        receiverId: config.near.score,
        actions: [
          {
            type: "FunctionCall",
            params: {
              methodName: "self_attest",
              deposit: parsedFee,
              gas: convertGas("200"),
              args: {
                valid_until: data.validUntil,
                trust_score: data.trust,
                signature: Array.from(hexToBytes(data.signature)),
              },
            },
          },
        ],
      });
      if (trx) {
        trxHash = trx.transaction_outcome.id;
        const successStatus = Object.prototype.hasOwnProperty.call(trx.status, "SuccessValue");
        if (successStatus) {
          await hapiApi.logAttest({ chain: -1, address, method: isUpdateMint ? 1 : 0 }); //method - 0 attest | method - 1 replace
          ToastService.update({ text: `Trust score is ${name}ed`, id: TOAST_ID.MINT, type: "success" });
          return true;
        } else return false;
      } else return false;
    }
    return false;
  } catch (error) {
    console.error(`Error, while ${name} trust score \n\n`, error);
    hapiApi.logError({
      nearAddress: address,
      action: isUpdateMint ? "UPDATE_MINT" : "MINT",
      error,
    });
    if (error instanceof Error) {
      ToastService.update({ text: error.message, id: TOAST_ID.MINT, icon });
    }
    return false;
  }
};

const mintNft = async ({
  selector,
  address,
  fee,
  openModal,
}: {
  selector: WalletSelector;
  address: string;
  fee: string;
  openModal: (params: NFTPurchasedParams) => void;
}) => {
  const icon = NetworkIconMap.NEAR;
  try {
    ToastService.loading({ text: t("Messages.LoadingAction", { name: "Mint NFT" }), id: TOAST_ID.MINT_NFT });
    const wallet = await selector.wallet();

    const rpcProvider = new NearRPCProviderService();
    const account = await rpcProvider.viewAccount(address);
    const accountBalance = account?.amount || "0";
    if (Big(accountBalance.toString()).lte(fee)) {
      ToastService.update({
        text: `Insufficient Balance - ${formatTokenAmount(fee, NEAR_TOKEN.decimals)} ${NEAR_TOKEN.symbol}`,
        id: TOAST_ID.MINT_NFT,
        icon,
      });
      return false;
    }
    const trx = await wallet.signAndSendTransaction({
      receiverId: config.near.nft,
      actions: [
        {
          type: "FunctionCall",
          params: {
            methodName: "nft_mint",
            args: {},
            gas: "45000000000000",
            deposit: fee,
          },
        },
      ],
    });

    if (trx) {
      const errors = getErrorsFromLogs([trx]);

      if (errors.length) {
        ToastService.update({ text: errors.join(", "), id: TOAST_ID.MINT_NFT, icon });
        return false;
      }

      const metadata: NFTMetadata = JSON.parse(
        atob((trx.status as FinalExecutionStatus)?.SuccessValue || EMPTY_STRING),
      );
      if (!metadata) throw Error(t("Messages.SomethingWentWrong"));

      openModal({ url: metadata.metadata.media, tokenId: metadata.token_id });
      ToastService.clear(TOAST_ID.MINT_NFT);
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error, while minting nft \n\n", error);

    if (error instanceof Error) {
      ToastService.update({ text: error.message, id: TOAST_ID.MINT_NFT, icon });
    }
    return false;
  }
};

const claimAirdrop = async ({
  selector,
  address,
  airdropId,
  nftIds,
  openModal,
}: {
  selector: WalletSelector;
  address: string;
  airdropId: number;
  nftIds: Array<string>;
  openModal: (id: number, numberOfNfts: number) => void;
}) => {
  const icon = NetworkIconMap.NEAR;
  try {
    const transactions = [];
    const { token_id } = useAirdrops.getState().airdrops[Network.NEAR][airdropId];
    const token = useTokens.getState().getTokenById(Network.NEAR, token_id);
    if (!token) return false;
    const wallet = await selector.wallet();
    ToastService.loading({ text: t("Messages.CheckingStorageBalance"), id: TOAST_ID.CLAIM_DROP });

    const storageTrx = await token.checkStorageBalance({ accountId: address });

    if (storageTrx) {
      const rpcProvider = new NearRPCProviderService();
      const account = await rpcProvider.viewAccount(address);
      const accountBalance = account?.amount || "0";
      const storageDeposit = (storageTrx["actions"][0] as FunctionCallAction).params.deposit;
      if (Big(accountBalance).lte(storageDeposit)) {
        const parsedStorageDeposit = formatTokenAmount(storageDeposit, NEAR_TOKEN.decimals);
        ToastService.update({
          text: `Insufficient Balance - ${parsedStorageDeposit} ${NEAR_TOKEN.symbol}`,
          id: TOAST_ID.MINT,
          icon,
        });
        return false;
      }

      try {
        await wallet.signAndSendTransaction(storageTrx);

        ToastService.update({
          text: t("Messages.HasStorageBalance"),
          id: TOAST_ID.CLAIM_DROP,
        });
        await delay(2500);
      } catch (e) {
        ToastService.update({
          text: t("Messages.MissingStorageBalance"),
          id: TOAST_ID.CLAIM_DROP,
        });
        return false;
      }
    }

    ToastService.update({
      text: t("Messages.LoadingAction", { name: "Claim Airdrop" }),
      id: TOAST_ID.CLAIM_DROP,
      isLoading: true,
    });

    const chunks = chunkArray(nftIds, MAX_IDS_PER_TRANSACTION);

    const generatedTransactions = (
      await Promise.all(
        chunks.map(async (chunk) => {
          const { data: response } = await hapiApi.getAirdropSignature({ address, nftIds: chunk });

          if ("signature" in response) {
            const signature = hexToBytes(response.signature);
            return NearAirdropContract.createClaimDropsByIdTransaction({
              signerId: address,
              receiverId: config.near.airdrop,
              nftIds: chunk,
              airdropId,
              signature: Array.from(signature),
              tokenBalance: response.balance,
            });
          } else {
            return null;
          }
        }),
      )
    ).filter(isNotNullOrUndefined);
    transactions.push(...generatedTransactions);

    const trx = [];
    for (const tx of generatedTransactions) {
      const txResult = await wallet.signAndSendTransaction(tx);
      await delay(2500);
      trx.push(txResult);
    }

    if (!trx) throw Error(t("Messages.SomethingWentWrong"));
    const lastTrx = trx[trx.length - 1];
    const successStatus = Object.prototype.hasOwnProperty.call(lastTrx?.status, "SuccessValue");
    if (!successStatus) {
      const errors = getErrorsFromLogs(trx);
      ToastService.update({ text: errors.join(", "), id: TOAST_ID.CLAIM_DROP, icon });
      return false;
    }

    openModal(airdropId, nftIds.length);
    ToastService.clear(TOAST_ID.CLAIM_DROP);

    return true;
  } catch (error) {
    console.error(`Error, while claim drop airdropId=${airdropId} nftId=${nftIds} \n\n`, error);

    if (error instanceof Error) {
      ToastService.update({ text: error.message, id: TOAST_ID.CLAIM_DROP, icon });
    } else if (error === "Reject") {
      ToastService.update({ text: error, id: TOAST_ID.CLAIM_DROP, icon });
    } else {
      ToastService.clear(TOAST_ID.CLAIM_DROP);
    }
    return false;
  }
};

const mintTotalScore = async ({
  selector,
  totalScore,
  signature,
  tgId,
  accountId,
  modal,
}: {
  selector: WalletSelector;
  totalScore: number;
  signature: string;
  tgId: number;
  accountId: string;
  modal: ModalContextProps;
}) => {
  const icon = NetworkIconMap.NEAR;

  const rpcProvider = new NearRPCProviderService();

  try {
    ToastService.loading({ text: t("Messages.LoadingAction", { name: "mint" }), id: TOAST_ID.TRUST_SCORE });
    const data = await NearTotalScoreContract.get_mint_data();
    if (!data) {
      ToastService.update({ text: "Can't get data from contract state", id: TOAST_ID.TRUST_SCORE, icon });
      return false;
    }
    const [mintCost, token] = data;
    const tokenContract = new FungibleTokenContract(token);

    const userTokenBalance = await tokenContract.getBalance({ accountId });
    if (!userTokenBalance) throw Error("Can`t get balance");

    if (Big(userTokenBalance).lt(mintCost)) {
      ToastService.update({ text: "You token balance lower that mint cost", id: TOAST_ID.TRUST_SCORE, icon });
      modal.warningModal.openModal(ModalType.BuyHapi, {
        hapiBalance: displayAmount(
          Big(mintCost)
            .minus(Big(userTokenBalance ?? "0"))
            .toFixed(),
          18,
        ),
        requiredBalance: displayAmount(mintCost, 18),
      });
      return false;
    }

    const account = await rpcProvider.viewAccount(accountId);
    const accountBalance = account?.amount || "0";

    if (Big(accountBalance).lt("0.01")) {
      ToastService.update({ text: "You need more NEAR balance", id: TOAST_ID.TRUST_SCORE, icon });

      return false;
    }

    const wallet = await selector.wallet();

    const trx = await wallet.signAndSendTransaction({
      receiverId: token,
      actions: [
        {
          type: "FunctionCall",
          params: {
            methodName: "ft_transfer_call",
            deposit: ONE_YOCTO,
            gas: convertGas("80"),
            args: {
              amount: Big(mintCost).toFixed(),
              receiver_id: config.near.totalScoreContract,
              msg: JSON.stringify({
                score: totalScore,
                signature: Array.from(hexToBytes(signature)),
                tg_id: `${tgId}`,
                user_id: accountId,
              }),
            },
          },
        },
      ],
    });
    if (trx) {
      const successStatus = Object.prototype.hasOwnProperty.call(trx.status, "SuccessValue");
      if (successStatus) {
        ToastService.update({ text: "Trust score is minted", id: TOAST_ID.TRUST_SCORE, type: "success" });
        return true;
      } else return false;
    } else return false;
  } catch (error) {
    console.error("Error while minting total score \n\n", error);
    ToastService.update({ text: "Error while minting total score", id: TOAST_ID.TRUST_SCORE, type: "error" });
    return false;
  }
};

export default {
  mintNft,
  mintScore,
  claimAirdrop,
  mintTotalScore,
};

export async function retrieveTokensMetadata(tokenAddresses: string[]): Promise<Tokens> {
  const tokensMetadata: (FungibleTokenContract | null)[] = await Promise.all(
    tokenAddresses.map(async (address: string) => {
      const ftTokenContract: FungibleTokenContract = new FungibleTokenContract(address);
      const metadata = await ftTokenContract.getMetadata();
      if (!metadata) return null;
      return ftTokenContract;
    }),
  );
  const tokensMetadataFiltered = tokensMetadata.filter(isNotNullOrUndefined);
  return toMap(tokensMetadataFiltered, "contractId");
}
